import React, { useEffect, useState } from 'react';
import { Cookie, Shield, BarChart3, Megaphone, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const STORAGE_KEY = 'pv_cookie_consent';
const CONSENT_VERSION = 1;

const DEFAULT_PREFS = {
  necessary: true,   // always on, cannot be disabled
  analytics: false,
  marketing: false,
};

const COPY = {
  it: {
    bannerTitle: 'Rispettiamo la tua privacy',
    bannerBody:
      'Usiamo i cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie analytics per migliorare l\'esperienza. Nessun dato sanitario è raccolto su questa pagina.',
    acceptAll: 'Accetta tutti',
    rejectAll: 'Rifiuta tutti',
    customize: 'Personalizza',
    save: 'Salva preferenze',
    privacyLink: 'Privacy policy',
    cookiePolicyLink: 'Cookie policy',
    preferences: 'Preferenze cookie',
    settingsTitle: 'Imposta le tue preferenze cookie',
    settingsBody:
      'Scegli quali categorie di cookie autorizzare. I cookie tecnici sono sempre attivi perché indispensabili al funzionamento del sito.',
    categories: {
      necessary: {
        name: 'Tecnici · necessari',
        desc: 'Permettono la navigazione di base e funzionalità essenziali (preferenze lingua, sicurezza). Non possono essere disattivati.',
        always: 'Sempre attivi',
      },
      analytics: {
        name: 'Analytics · anonimi',
        desc: 'Ci aiutano a capire come viene usato il sito (pagine viste, tempo di permanenza) per migliorarlo. Dati aggregati e anonimi.',
      },
      marketing: {
        name: 'Marketing',
        desc: 'Usati per misurare l\'efficacia delle nostre campagne e mostrare contenuti rilevanti. Non condividiamo i tuoi dati con terze parti senza consenso.',
      },
    },
  },
  en: {
    bannerTitle: 'We respect your privacy',
    bannerBody:
      'We use strictly necessary cookies to run this site and, with your consent, analytics cookies to improve the experience. No health data is collected on this page.',
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    customize: 'Customize',
    save: 'Save preferences',
    privacyLink: 'Privacy policy',
    cookiePolicyLink: 'Cookie policy',
    preferences: 'Cookie preferences',
    settingsTitle: 'Set your cookie preferences',
    settingsBody:
      'Choose which categories of cookies to allow. Necessary cookies are always on as they are essential for the site to work.',
    categories: {
      necessary: {
        name: 'Strictly necessary',
        desc: 'Required for basic navigation and essential functions (language preference, security). Cannot be disabled.',
        always: 'Always on',
      },
      analytics: {
        name: 'Analytics · anonymous',
        desc: 'Help us understand how the site is used (pages viewed, time on page) so we can improve it. Aggregated and anonymous.',
      },
      marketing: {
        name: 'Marketing',
        desc: 'Used to measure campaign performance and show relevant content. We never share your data with third parties without consent.',
      },
    },
  },
};

const loadConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveConsent = (prefs) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: CONSENT_VERSION, prefs, ts: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
  // Fire a custom event so analytics/marketing scripts can hook later
  try {
    window.dispatchEvent(new CustomEvent('pv:cookie-consent', { detail: prefs }));
  } catch {
    /* ignore */
  }
};

export const CookieBanner = () => {
  const { locale } = useLanguage();
  const t = COPY[locale] || COPY.it;

  const [show, setShow] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  // On mount: show banner if no valid consent saved
  useEffect(() => {
    const saved = loadConsent();
    if (!saved) {
      setShow(true);
    } else {
      setPrefs({ ...DEFAULT_PREFS, ...saved.prefs });
    }
  }, []);

  // Notify other UI (chatbot, sticky mobile CTA) about banner visibility
  // so they can hide themselves until the user closes/accepts the banner.
  useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent('pv:cookie-banner-visible', { detail: { visible: show } })
      );
    } catch {
      /* ignore */
    }
  }, [show]);

  // Listen for footer "Preferenze cookie" reopener
  useEffect(() => {
    const onReopen = () => {
      const saved = loadConsent();
      if (saved) setPrefs({ ...DEFAULT_PREFS, ...saved.prefs });
      setShowSettings(true);
      setShow(true);
    };
    window.addEventListener('pv:open-cookie-settings', onReopen);
    return () => window.removeEventListener('pv:open-cookie-settings', onReopen);
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, analytics: true, marketing: true };
    setPrefs(next);
    saveConsent(next);
    setShow(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const next = { necessary: true, analytics: false, marketing: false };
    setPrefs(next);
    saveConsent(next);
    setShow(false);
    setShowSettings(false);
  };

  const savePrefs = () => {
    saveConsent(prefs);
    setShow(false);
    setShowSettings(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pv-cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed left-2 right-2 bottom-2 sm:left-4 sm:right-4 sm:bottom-4 lg:left-6 lg:right-auto lg:max-w-[480px] z-[9998] animate-pv-fade-up"
    >
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.30)] overflow-hidden">
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-pv-teal/12 blur-2xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-pv-orange/10 blur-2xl pointer-events-none" />

        {/* ----- Banner mode (ultra-compact <sm, compact sm+, full lg+) ----- */}
        {!showSettings && (
          <>
            {/* MOBILE <sm — single-row icon + 3 actions, no text body */}
            <div className="sm:hidden relative p-1.5 pr-2">
              <div className="flex items-center gap-1.5">
                <div
                  className="shrink-0 w-7 h-7 rounded-md bg-pv-ice border border-pv-teal/25 flex items-center justify-center"
                  aria-hidden="true"
                  title={t.bannerTitle}
                >
                  <Cookie className="w-3.5 h-3.5 text-pv-teal" />
                </div>
                <button
                  onClick={acceptAll}
                  data-testid="pv-cookie-accept-all"
                  className="pv-btn-primary !py-1.5 !px-3 !text-[11px] flex-1 justify-center whitespace-nowrap"
                >
                  {t.acceptAll}
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="pv-cookie-reject-all"
                  className="pv-btn-ghost !py-1.5 !px-2.5 !text-[11px] justify-center whitespace-nowrap"
                >
                  {t.rejectAll}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  data-testid="pv-cookie-customize"
                  aria-label={t.customize}
                  className="shrink-0 w-7 h-7 rounded-full border border-slate-200 bg-white text-slate-500 hover:text-pv-teal hover:border-pv-teal/30 transition-colors inline-flex items-center justify-center"
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* TABLET+DESKTOP sm+ — original compact / full layout */}
            <div className="hidden sm:block relative p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="shrink-0 w-8 h-8 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-pv-ice border border-pv-teal/25 flex items-center justify-center">
                  <Cookie className="w-4 h-4 lg:w-5 lg:h-5 text-pv-teal" />
                </div>
                <div className="flex-1 min-w-0 pr-6">
                  <h3
                    id="cookie-banner-title"
                    className="font-heading font-bold text-pv-ink text-[14px] lg:text-[16px] leading-tight"
                  >
                    {t.bannerTitle}
                  </h3>
                  <p className="mt-1.5 text-[12px] lg:text-[13px] text-slate-600 leading-snug lg:leading-relaxed line-clamp-2 lg:line-clamp-none">
                    {t.bannerBody}
                  </p>
                </div>
                <button
                  onClick={rejectAll}
                  aria-label="Close"
                  className="absolute top-3 right-3 w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="hidden lg:flex items-center gap-3 text-[11px] text-slate-500 mb-4 flex-wrap">
                <a
                  href="https://www.primevision.app/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-pv-teal font-semibold underline-offset-2 hover:underline"
                >
                  {t.privacyLink}
                </a>
                <span className="text-slate-300">·</span>
                <a
                  href="https://www.primevision.app/cookie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-pv-teal font-semibold underline-offset-2 hover:underline"
                >
                  {t.cookiePolicyLink}
                </a>
              </div>

              <div className="flex flex-row items-center gap-2 lg:gap-2.5">
                <button
                  onClick={acceptAll}
                  data-testid="pv-cookie-accept-all-tablet"
                  className="pv-btn-primary !py-2.5 !px-4 !text-[12.5px] lg:!text-[13px] flex-1 justify-center whitespace-nowrap"
                >
                  {t.acceptAll}
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="pv-cookie-reject-all-tablet"
                  className="pv-btn-ghost !py-2.5 !px-4 !text-[12.5px] lg:!text-[13px] justify-center whitespace-nowrap"
                >
                  {t.rejectAll}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  data-testid="pv-cookie-customize-tablet"
                  className="inline-flex items-center justify-center gap-1 px-3 lg:px-4 py-2.5 rounded-full text-[12.5px] lg:text-[13px] font-bold text-slate-600 hover:text-pv-teal transition-colors whitespace-nowrap"
                >
                  {t.customize}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        )}

        {/* ----- Settings mode ----- */}
        {showSettings && (
          <div className="relative p-5 sm:p-6 max-h-[80vh] overflow-y-auto pv-no-scrollbar">
            <div className="flex items-start gap-3 mb-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-pv-ice border border-pv-teal/25 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-pv-teal" />
              </div>
              <div className="flex-1 pr-6">
                <h3 className="font-heading font-bold text-pv-ink text-[16px] leading-tight">
                  {t.settingsTitle}
                </h3>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">
                  {t.settingsBody}
                </p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                aria-label="Close settings"
                className="absolute top-3 right-3 w-7 h-7 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              <CategoryRow
                icon={Shield}
                title={t.categories.necessary.name}
                desc={t.categories.necessary.desc}
                checked
                disabled
                badge={t.categories.necessary.always}
                testId="pv-cookie-cat-necessary"
              />
              <CategoryRow
                icon={BarChart3}
                title={t.categories.analytics.name}
                desc={t.categories.analytics.desc}
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="pv-cookie-cat-analytics"
              />
              <CategoryRow
                icon={Megaphone}
                title={t.categories.marketing.name}
                desc={t.categories.marketing.desc}
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="pv-cookie-cat-marketing"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={savePrefs}
                data-testid="pv-cookie-save"
                className="pv-btn-primary !py-2.5 !px-4 !text-[13px] flex-1 justify-center"
              >
                {t.save}
              </button>
              <button
                onClick={acceptAll}
                data-testid="pv-cookie-accept-all-settings"
                className="pv-btn-ghost !py-2.5 !px-4 !text-[13px] justify-center"
              >
                {t.acceptAll}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryRow = ({ icon: Icon, title, desc, checked, disabled, onChange, badge, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-xl border p-3.5 transition-colors ${
      checked && !disabled ? 'bg-pv-ice/60 border-pv-teal/25' : 'bg-slate-50 border-slate-200'
    } ${disabled ? 'opacity-90' : ''}`}
  >
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
        <Icon className="w-4 h-4 text-pv-teal" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="font-heading font-bold text-[13.5px] text-pv-ink leading-tight">
            {title}
          </div>
          {disabled ? (
            <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-pv-teal/10 border border-pv-teal/25 text-[9.5px] font-mono uppercase tracking-[0.16em] font-bold text-pv-teal">
              {badge}
            </span>
          ) : (
            <Toggle checked={checked} onChange={onChange} />
          )}
        </div>
        <p className="text-[12px] text-slate-600 leading-snug">{desc}</p>
      </div>
    </div>
  </div>
);

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange?.(!checked)}
    role="switch"
    aria-checked={checked}
    className={`shrink-0 relative inline-flex items-center w-10 h-6 rounded-full transition-colors ${
      checked ? 'bg-pv-teal' : 'bg-slate-300'
    }`}
  >
    <span
      className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-1'
      }`}
    />
  </button>
);

export default CookieBanner;
